
import React, { useEffect, useRef, useState } from "react";
import { SlidersHorizontal, Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { resetSidebarState } from "../redux/sidebarSlice";
import { useDispatch } from "react-redux";
import Logo from "../assets/img/ThinkStore_Logo.png";

const tabs = [
  "All",
  "Machine Learning",
  "Deep Learning",
  "LLM",
  "Gen AI",
  "Agentic AI",
];

const filterOptions = [
  "Finance",
  "Transport",
  "Meteorology",
  "Healthcare",
  "Real Estate",
  "Security",
  "Law",
  "Infrastructure",
  "Manufacturing",
  "Agriculture",
];

const Topbar = ({
  activeTab,
  setActiveTab,
  selectedFilter,
  setSelectedFilter,
}) => {
  const location = useLocation();
  const showTabs = location.pathname === "/user/dashboard";

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    dispatch(resetSidebarState());
    navigate("/");
  };

  const filterBtnRefMobile = useRef(null);
  const filterBtnRefDesktop = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!filterBtnRefMobile.current ||
          !filterBtnRefMobile.current.contains(event.target)) &&
        (!filterBtnRefDesktop.current ||
          !filterBtnRefDesktop.current.contains(event.target))
      ) {
        setShowFilterDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleIdeaGeneraterClick = () => {
    window.open("https://thinkstore.nextastra.com/idea_generator", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="sticky top-0 w-full border-b border-blue-custom bg-white shadow z-10">
      {/* Topbar Row */}
      <div className="flex items-center justify-between pr-2 md:pr-8">
        {/* Logo only on md+ */}
        <div className="hidden md:block text-lg md:text-xl font-semibold textcolor">
          <img
            src={Logo}
            className="w-80 aspect-[5/1] object-contain"
            alt="Logo"
          />
        </div>

        {/* Mobile Logo on left (only mobile) */}
        <div className="md:hidden text-lg font-semibold textcolor pl-1">
          <img
            src={Logo}
            className="w-40 aspect-[3/1] object-contain"
            alt="Logo"
          />
        </div>

        {/* Mobile only: Idea, Filter, Hamburger on right */}
        <div className="flex md:hidden items-center gap-2">
          <div
            className="cursor-pointer px-[0.7rem] py-[0.3rem] rounded-[0.4rem] text-[#ffc107] bg-[#2383CD]"
            onClick={handleIdeaGeneraterClick}
            title="Idea Generator"
          >
            <i className="bi bi-lightbulb-fill"></i>
          </div>

          <button
            ref={filterBtnRefMobile}
            className="bg-[#48484833] p-2 rounded-[0.4rem]"
            onClick={() => setShowFilterDropdown((prev) => !prev)}
          >
            <SlidersHorizontal className="textblack" size={18} />
          </button>

          {showTabs && (
            <button
              className="bg-[#48484833] p-2 rounded-md"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          )}
        </div>

        {/* Center - Tabs only on md+ */}
        <div className="hidden md:flex gap-2 p-1 rounded-full text-sm">
          {showTabs &&
            tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setSelectedFilter(null);
                }}
                className={`px-4 py-2 rounded-full transition ${
                  activeTab === tab
                    ? "bgbtn text-white"
                    : "textblack hover:bg-gray-200"
                }`}
              >
                {tab}
              </button>
            ))}
        </div>

        {/* Right side on md+: Idea, Filter, Logout */}
        <div className="hidden md:flex items-center gap-3">
          <div
            className="cursor-pointer px-[0.7rem] py-[0.3rem] rounded-[0.4rem] text-[#ffc107] bg-[#2383CD]"
            onClick={handleIdeaGeneraterClick}
            title="Idea Generator"
          >
            <i className="bi bi-lightbulb-fill"></i>
          </div>

          <button
            ref={filterBtnRefDesktop}
            className="bg-[#48484833] p-2 rounded-[0.4rem] hidden md:block"
            onClick={() => setShowFilterDropdown((prev) => !prev)}
          >
            <SlidersHorizontal className="textblack" size={18} />
          </button>

          <button
            className="bg-[#48484833] px-2 py-0 rounded-[0.4rem]"
            onClick={handleLogout}
            title="Logout"
          >
            <i className="bi bi-box-arrow-right fs-5 text-dark"></i>
          </button>
        </div>
      </div>

      {/* Filter Dropdown (Desktop & Mobile) */}
      {showFilterDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-12 right-0 w-48 bg-[#00000080] text-white rounded-lg shadow-lg border border-[#3c3c3c] z-50
               max-h-48 overflow-y-auto
               sm:max-h-56
               md:max-h-64"
        >
          {filterOptions.map((option) => (
            <button
              key={option}
              className={`w-full text-left px-4 py-3 hover:bg-[#2c2c2c] transition ${
                selectedFilter === option
                  ? "bgbtn1 font-semibold rounded-md"
                  : ""
              }`}
              onClick={() => {
                setSelectedFilter(option);
                setShowFilterDropdown(false);
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}

      {/* Mobile Menu Content (tabs + filter + logout) */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white shadow-inner border-t">
          {/* Tabs */}
          {showTabs && (
            <div className="flex flex-col p-2">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setSelectedFilter(null); // clear selected filter here
                    setMobileMenuOpen(false);
                  }}
                  className={`px-4 py-2 rounded-md text-left ${
                    activeTab === tab ? "bgbtn text-white" : "hover:bg-gray-100"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          )}

          {/* Mobile Actions */}
          <div className="border-t flex flex-col">
            <button
              className="px-4 py-2 flex items-center gap-2 hover:bg-gray-100"
              onClick={handleLogout}
            >
              <i className="bi bi-box-arrow-right fs-5 text-dark"></i>Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Topbar;
